import { ReactNode } from 'react';
import { create } from 'zustand';

type AlertType = 'error' | 'info' | 'confirm';

interface useAlertModalStore {
  isOpen: boolean;
  title: string;
  description: string;
  type: AlertType;
  loading: boolean;
  contents?: string | ReactNode;
  isDelete?: boolean;
  width?: string;
  open: (params: {
    type: AlertType;
    title: string;
    description: string;
    contents?: string | ReactNode;
    width?: string;
    onConfirm?: () => Promise<void>;
    isDelete?: boolean;
  }) => void;
  onConfirm: () => void;
  close: () => void;
  callback: () => Promise<void>;
  openInfo: (params: {
    title: string;
    description: string;
    onConfirm?: () => Promise<void>;
  }) => void;
  openWarning: (params: {
    title: string;
    description: string;
    contents?: string | ReactNode;
    onConfirm: () => Promise<void>;
  }) => void;
  openDelete: (params: {
    title?: string;
    description?: string;
    onConfirm: () => Promise<void>;
  }) => void;
  openConfirm: (params: {
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
  }) => void;
}

export const useAlertModal = create<useAlertModalStore>((set, get) => ({
  isOpen: false,
  isDelete: false,

  open: ({
    type,
    title,
    description,
    contents,
    onConfirm,
    isDelete,
    width
  }) => {
    set({
      type,
      title,
      description,
      contents,
      isOpen: true,
      callback: onConfirm,
      isDelete,
      width: width ?? 'w-auto'
    });
  },
  close: () => set({ isOpen: false, contents: '' }, false),
  width: 'w-auto',
  title: '',
  description: '',
  contents: '',
  type: 'error',
  loading: false,
  onConfirm: async () => {
    const cb = get().callback;
    if (cb) {
      set({ loading: true });
      try {
        await cb();
      } catch (error) {
        console.log(error);
      } finally {
        set({ loading: false });
      }
    } else {
      set({ isOpen: false });
    }
  },
  callback: () => Promise.resolve(),
  openInfo: ({ title, description, onConfirm }) => {
    const { open } = get();
    open({
      type: 'info',
      title: title,
      description: description,
      onConfirm
    });
  },
  openWarning: ({ title, description, contents, onConfirm }) => {
    const { open } = get();
    open({
      type: 'error',
      title,
      description,
      contents,
      onConfirm
    });
  },
  openDelete: ({ title, description, onConfirm }) => {
    const { openWarning } = get();
    openWarning({
      title: title || '삭제하시겠습니까?',
      description: description || '삭제 후 되돌릴 수 없습니다.',
      onConfirm
    });
  },
  openConfirm: ({ title, description, onConfirm }) => {
    const { open } = get();
    open({
      type: 'confirm',
      title: title,
      description: description,
      onConfirm
    });
  }
}));
